import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Calendar,
  Dropdown,
  Button,
  DatePicker,
  Empty,
  Input,
  Modal,
  Space,
  Spin,
  Pagination,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  Alert,
  message,
} from 'antd'
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  SearchOutlined,
  EditOutlined,
  LoadingOutlined,
  FileSearchOutlined,
  DownOutlined,
  UpOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import { supplyCardPath } from '../model'
import { Delivery, deliveryStatus, isDeliveryAccepted, canReceiveDelivery } from './model'
import { supplyRequest } from './api'
import DeliveryTotals from './totals'
import SupplierFiles from './supplier-files'
import SupplierOrders from './supplier-orders'
import DeliveryIntakeModal from './intake-modal'
import { analysisLabel, analysisPath, savedAnalysisState } from './analysis'
import { useDeliveryAnalyses } from './use-analyses'
import './styles.css'

const quantity = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
const deliveryAmount = (metric: Delivery['amount'], delivery: Delivery) => (
  <Tooltip
    title={
      !metric || metric.value == null
        ? 'В 1С нет данных для расчёта суммы ожидаемых позиций.'
        : metric.missing
        ? `Сумма известна только для части позиций. Нет данных для ${metric.missing} позиций.`
        : delivery.received_in_onec
        ? 'Сумма полученных позиций по ценам заказа поставщику. Поступления доступны в карточке заказа.'
        : 'Сумма ожидаемых позиций по данным 1С. Для частичной поставки учитывается оставшееся количество.'
    }
  >
    <span
      className={!metric || metric.missing ? 'delivery-calendar__metric-incomplete' : undefined}
    >
      {metric?.value == null
        ? '—'
        : `${metric.value.toLocaleString('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}${delivery.currency ? ` ${delivery.currency}` : ''}`}
      {metric?.value != null && !!metric.missing && <small> · частично</small>}
    </span>
  </Tooltip>
)
const measure = (metric: Delivery['weight'], unit: string) => (
  <Tooltip
    title={
      metric.missing
        ? `Нет данных о ${unit} в 1С для ${metric.missing} позиций. ${
            metric.value == null
              ? 'Итог пока неизвестен.'
              : 'Показан итог только по заполненным позициям.'
          }`
        : undefined
    }
  >
    <span className={metric.missing ? 'delivery-calendar__metric-incomplete' : undefined}>
      {metric.value == null ? '—' : quantity(metric.value)}
      {metric.value != null && !!metric.missing && <small> · частично</small>}
    </span>
  </Tooltip>
)

export const DeliveryCalendarPage: React.FC = () => {
  const { search } = useLocation()
  const navigate = useNavigate()
  const query = useMemo(() => new URLSearchParams(search), [search])
  const queryDate = dayjs(query.get('date'))
  const date =
    queryDate.isValid() && /^\d{4}-\d{2}-\d{2}$/.test(query.get('date') || '') ? queryDate : dayjs()
  const selectedDate = date.format('YYYY-MM-DD')
  const month = date.format('YYYY-MM')
  const tab = 'day'
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [today, setToday] = useState(dayjs().format('YYYY-MM-DD'))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [stats, setStats] = useState({ planned: 0, accepted: 0, moved: 0, late: 0 })
  const [chooseSupplier, setChooseSupplier] = useState(false)
  const [filter, setFilter] = useState('')
  const [intake, setIntake] = useState<Delivery | null>(null)
  const [plan, setPlan] = useState<Delivery | null>(null)
  const [planDate, setPlanDate] = useState<string | null>(null)
  const [savingPlan, setSavingPlan] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [loadedAt, setLoadedAt] = useState('')
  const [dayCounts, setDayCounts] = useState<Record<string, number>>({})
  const [counts, setCounts] = useState({ day: 0, overdue: 0, undated: 0 })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [warning, setWarning] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [opening, setOpening] = useState(false)
  const requestVersion = useRef(0)
  const [analysesOpen, setAnalysesOpen] = useState(false)
  const [showAllAnalyses, setShowAllAnalyses] = useState(false)
  const load = useCallback(
    async (refresh = false, background = false) => {
      const version = ++requestVersion.current
      if (!background) setLoading(true)
      setError('')
      try {
        const result = await supplyRequest(
          `/api/supply/deliveries?${new URLSearchParams({
            month,
            date: selectedDate,
            tab,
            page: String(page),
            page_size: String(pageSize),
            search: searchTerm,
            grouping: 'supplier',
            ...(supplierFilter ? { supplier: supplierFilter } : {}),
            ...(refresh ? { refresh: '1' } : {}),
          })}`,
          { timeoutMs: 180000 },
        )
        if (!Array.isArray(result.data) || result.meta?.complete !== true)
          throw new Error('Не удалось получить полный календарь. Обновите данные')
        if (requestVersion.current !== version) return
        setDeliveries(result.data)
        setSelectedKeys(keys => keys.filter(key => !result.data.some((row: Delivery) => row.key === key && !canReceiveDelivery(row))))
        setSuppliers(result.meta.suppliers || [])
        setStats(result.meta.statuses)
        setToday(result.meta.today)
        setLoadedAt(result.meta.loaded_at)
        setDayCounts(result.meta.days)
        setCounts(result.meta.counts)
        setTotal(result.meta.total)
        setCurrentPage(result.meta.page)
        setWarning(result.meta.warning || '')
        setRefreshing(result.meta.refreshing === true)
      } catch (cause: any) {
        if (requestVersion.current === version)
          setError(cause.message || 'Не удалось загрузить календарь')
      } finally {
        if (requestVersion.current === version) setLoading(false)
      }
    },
    [month, selectedDate, tab, page, pageSize, searchTerm, supplierFilter],
  )
  const analysis = useDeliveryAnalyses(() => void load(false, true))
  const processingCount = analysis.analyses.filter(
    row => (analysis.states[row.review_id] || savedAnalysisState(row)).status === 'processing',
  ).length
  const readyCount = analysis.analyses.filter(
    row => (analysis.states[row.review_id] || savedAnalysisState(row)).status === 'review',
  ).length
  const failedCount = analysis.analyses.filter(
    row => analysis.states[row.review_id]?.status === 'failed',
  ).length
  useEffect(() => {
    void load()
    return () => {
      requestVersion.current++
    }
  }, [load])
  useEffect(() => {
    if (!refreshing || loading) return
    const timer = setTimeout(() => void load(false, true), 2000)
    return () => clearTimeout(timer)
  }, [refreshing, load, deliveries, loading])
  useEffect(() => {
    const timer = setTimeout(() => {
      setSelectedKeys([])
      setSearchTerm(filter.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [filter])
  const changeQuery = (values: Record<string, string>) => {
    setPage(1)
    setSupplierFilter(''); setSelectedKeys([])
    const next = new URLSearchParams(search)
    Object.entries(values).forEach(([key, value]) => next.set(key, value))
    navigate({ search: next.toString() })
  }
  const rows = deliveries
  const awaitingSuppliers = suppliers.filter(supplier => rows.some(row =>
    (row as any).supplier_key === supplier.key && canReceiveDelivery(row),
  ))
  const openIntake = async (delivery: Delivery) => {
    if (delivery.received_in_onec) {
      navigate(supplyCardPath(delivery.dataset, delivery.document_number))
      return
    }
    if (isDeliveryAccepted(delivery)) {
      const accepted = delivery.intakes.find(intake => intake.status === 'checked' && intake.accepted_delivery_keys?.includes(delivery.key))!
      navigate(analysisPath(accepted.review_id, { status: 'checked', documentId: accepted.document_id }))
      return
    }
    if (opening) return
    setOpening(true)
    try {
      const result = await supplyRequest(`/api/supply/deliveries/${delivery.key}`)
      setIntake(result.data)
    } catch (cause: any) {
      message.error(cause.message || 'Не удалось открыть поставку')
    } finally {
      setOpening(false)
    }
  }
  const openGroup = async (supplier: { key: string; supplier: string }) => {
    if (opening) return
    setOpening(true)
    try {
      const result = await supplyRequest(`/api/supply/delivery-groups/${supplier.key}?date=${selectedDate}`, { timeoutMs: 180000 })
      const deliveries: Delivery[] = result.data
      const intakes = [...new Map(deliveries.flatMap(row => row.intakes).map(row => [row.id, row])).values()]
      setIntake({ ...deliveries[0], deliveries, intakes, uploadMode: true })
      setChooseSupplier(false)
    } catch (cause: any) { message.error(cause.message || 'Не удалось открыть поставщика') }
    finally { setOpening(false) }
  }
  const editPlan = (delivery: Delivery) => {
    setPlan(delivery)
    setPlanDate(delivery.planned_date)
  }
  const savePlan = async (reset = false) => {
    if (!plan || savingPlan) return
    setSavingPlan(true)
    try {
      await supplyRequest(`/api/supply/deliveries/${plan.key}/plan`, {
        method: 'PUT',
        body: JSON.stringify({ planned_date: planDate, version: plan.plan_version, reset }),
      })
      setPlan(null)
      message.success('Дата поставки сохранена в ERP')
      await load()
    } catch (cause: any) {
      message.error(cause.message)
    } finally {
      setSavingPlan(false)
    }
  }
  const columns: any[] = [
    {
      title: 'Состояние',
      key: 'supplier',
      dataIndex: 'supplier',
      width: 180,
      maxLines: null,
      render: (_: any, item: Delivery) => {
        const latest = item.intakes.find(intake => intake.document_count > 0) || item.intakes[0]
        const state = latest && analysis.states[latest.review_id]
        const status = deliveryStatus(item)
        const documents = item.intakes.flatMap(intake => (intake.documents || []).map(document => ({ ...document, reviewId: intake.review_id, createdAt: intake.created_at })))
        const reviewReady = !!latest?.document_count
        return (
          <div className="delivery-calendar__supplier">
            <Space size={4} wrap>
              <Tag
                color={status.color}
                icon={state?.status === 'processing' ? <LoadingOutlined /> : undefined}
              >
                {status.text}
              </Tag>
              {latest && reviewReady && (documents.length > 1 ? (
                <Dropdown trigger={['click']} menu={{ items: documents.map(document => ({ key: `${document.reviewId}:${document.id}`,
                  label: `${document.number ? `№ ${document.number}` : document.file} · ${document.lines} строк${document.createdAt && dayjs(document.createdAt).isValid() ? ` · ${dayjs(document.createdAt).format('DD.MM HH:mm')}` : ''}`, icon: <FileSearchOutlined />,
                  onClick: () => navigate(analysisPath(document.reviewId, { status: 'review', documentId: document.id })) })) }}>
                  <Button size="small" icon={<FileSearchOutlined />} onClick={event => event.stopPropagation()}>Накладные ({documents.length})</Button>
                </Dropdown>
              ) : (
                <Button size="small" icon={<FileSearchOutlined />} onClick={event => {
                  event.stopPropagation()
                  navigate(analysisPath(latest.review_id, { status: latest.status, documentId: latest.document_id }))
                }}>Открыть накладную</Button>
              ))}
              {latest && state?.status === 'failed' && (
                <Button
                  size="small"
                  onClick={event => {
                    event.stopPropagation()
                    navigate(analysisPath(latest.review_id))
                  }}
                >
                  Открыть разбор
                </Button>
              )}
              {!item.received_in_onec && <Tooltip title="Изменить плановую дату в ERP">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label={`Изменить дату поставки ${item.document_number}`}
                  onClick={event => {
                    event.stopPropagation()
                    editPlan(item)
                  }}
                />
              </Tooltip>}
              {item.received_in_onec && item.received_at && <Typography.Text type="secondary">
                Поступление {dayjs(item.received_at).format('DD.MM.YYYY')}
              </Typography.Text>}
            </Space>
          </div>
        )
      },
    },
    { title: 'Позиций', dataIndex: 'position_count', align: 'right', width: 60 },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      align: 'right',
      width: 140,
      render: deliveryAmount,
    },
    {
      title: 'Вес, кг',
      dataIndex: 'weight',
      align: 'right',
      width: 80,
      render: (metric: Delivery['weight']) => measure(metric, 'весе'),
    },
    {
      title: 'Площадь, м²',
      dataIndex: 'area',
      align: 'right',
      width: 90,
      render: (metric: Delivery['area']) => measure(metric, 'площади'),
    },
    {
      title: 'Заказ поставщику',
      key: 'supplier_order',
      width: 160,
      maxLines: null,
      render: (_: any, item: Delivery) => item.dataset === 'orders' ? (
        <Link
          onClick={event => event.stopPropagation()}
          to={supplyCardPath(item.dataset, item.document_number)}
        >
          № {item.document_number}
        </Link>
      ) : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Заявка на закупку',
      key: 'purchase_request',
      width: 160,
      maxLines: null,
      render: (_: any, item: Delivery) => {
        const requests = [...new Set([
          ...(item.dataset === 'requests' ? [item.document_number] : []),
          ...(item.requests || []).map(request => request.number),
        ].filter(Boolean))]
        return requests.length ? <Space direction="vertical" size={2}>
          {requests.map(number => <Link key={number} to={supplyCardPath('requests', number)} onClick={event => event.stopPropagation()}>№ {number}</Link>)}
        </Space> : <Typography.Text type="secondary">—</Typography.Text>
      },
    },
  ]
  const displayColumns = [columns[6], columns[5], columns[0], ...columns.slice(1, 5)]
  const groups = suppliers.filter(supplier => rows.some(row => (row as any).supplier_key === supplier.key))
  return (
    <B2BLayout
      breadcrumbs={[{ href: '/warehouse', title: 'Склад' }, { title: 'Календарь поставок' }]}
      pageTitle="Календарь поставок"
    >
      <div className="delivery-calendar">
        <div className="delivery-calendar__toolbar">
          <Space wrap>
            <Button
              icon={<LeftOutlined />}
              aria-label="Предыдущий месяц"
              onClick={() =>
                changeQuery({
                  date: date.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                  tab: 'day',
                })
              }
            />
            <DatePicker
              picker="month"
              value={date}
              allowClear={false}
              format="MMMM YYYY"
              onChange={value =>
                value &&
                changeQuery({ date: value.startOf('month').format('YYYY-MM-DD'), tab: 'day' })
              }
            />
            <Button
              icon={<RightOutlined />}
              aria-label="Следующий месяц"
              onClick={() =>
                changeQuery({
                  date: date.add(1, 'month').startOf('month').format('YYYY-MM-DD'),
                  tab: 'day',
                })
              }
            />
            <Button onClick={() => changeQuery({ date: today, tab: 'day' })}>Сегодня</Button>
          </Space>
          <Space wrap>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(true)}>
              Обновить
            </Button>
            <Button onClick={() => navigate('/warehouse/incoming')}>Входящие накладные</Button>
            <Button type="primary" icon={<UploadOutlined />} disabled={loading || !awaitingSuppliers.length} onClick={() => setChooseSupplier(true)}>Принять поставку</Button>
          </Space>
        </div>
        {!!analysis.analyses.length && (
          <section className="delivery-calendar__analyses" aria-label="Разборы документов поставок">
            <div className="delivery-calendar__analyses-header">
              <Space wrap>
                <Typography.Text strong>Разборы документов</Typography.Text>
                {!!processingCount && (
                  <Tag color="blue" icon={<LoadingOutlined />}>
                    В работе: {processingCount}
                  </Tag>
                )}
                {!!readyCount && <Tag color="gold">Готово к проверке: {readyCount}</Tag>}
                {!!failedCount && <Tag color="red">Не удалось разобрать: {failedCount}</Tag>}
              </Space>
              <Button
                type="text"
                icon={analysesOpen ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setAnalysesOpen(value => !value)}
              >
                {analysesOpen ? 'Свернуть' : 'Показать'}
              </Button>
            </div>
            {analysesOpen && (
              <>
                <p className="delivery-calendar__muted">
                  Можно загружать документы следующей поставки. Статусы обновляются автоматически.
                </p>
                <div className="delivery-calendar__analysis-list">
                  {analysis.analyses.slice(0, showAllAnalyses ? undefined : 5).map(row => {
                    const state = analysis.states[row.review_id] || savedAnalysisState(row),
                      label = analysisLabel(state)
                    return (
                      <div className="delivery-calendar__analysis" key={row.review_id}>
                        <div>
                          <strong>
                            {row.supplier || 'Поставщик не указан'} · {row.files?.map(file => file.name).join(', ') || `№ ${row.document_number}`}
                          </strong>
                          <small>
                            {dayjs(row.arrived_at).format('DD.MM.YYYY HH:mm')}
                            {state.documentCount
                              ? ` · проверено ${state.checkedCount || 0} из ${
                                  state.documentCount
                                } накладных`
                              : ''}
                          </small>
                        </div>
                        <Space wrap>
                          <Tag
                            color={label.color}
                            icon={state.status === 'processing' ? <LoadingOutlined /> : undefined}
                          >
                            {label.text}
                          </Tag>
                          {['review', 'checked'].includes(state.status) && (
                            <Button
                              size="small"
                              icon={<FileSearchOutlined />}
                              onClick={() => navigate(analysisPath(row.review_id, state))}
                            >
                              {state.status === 'checked'
                                ? 'Открыть результат'
                                : 'Проверить накладные'}
                            </Button>
                          )}
                          {state.status === 'failed' && (
                            <Button
                              size="small"
                              onClick={() => navigate(analysisPath(row.review_id))}
                            >
                              Открыть разбор
                            </Button>
                          )}
                          {state.status === 'upload' && (
                            <Button
                              size="small"
                              onClick={() => void openIntake({ key: row.delivery_key } as Delivery)}
                            >
                              Загрузить документы
                            </Button>
                          )}
                        </Space>
                      </div>
                    )
                  })}
                </div>
                {analysis.analyses.length > 5 && (
                  <Button type="text" onClick={() => setShowAllAnalyses(value => !value)}>
                    {showAllAnalyses
                      ? 'Показать последние 5'
                      : `Показать все (${analysis.analyses.length})`}
                  </Button>
                )}
              </>
            )}
          </section>
        )}
        {analysis.error && (
          <Alert
            type="warning"
            showIcon
            message={analysis.error}
            action={
              <Button size="small" onClick={analysis.refresh}>
                Обновить статусы
              </Button>
            }
          />
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message="Календарь не загружен"
            description={error}
            action={<Button onClick={() => load(true)}>Повторить</Button>}
          />
        )}
        {warning && <Alert type="warning" showIcon message={warning} />}
        <Spin
          spinning={loading || opening}
          tip={opening ? 'Открываем поставку…' : 'Загружаем поставки…'}
        >
          <div className="delivery-calendar__workspace">
            <aside className="delivery-calendar__sidebar">
              <div className="delivery-calendar__month">
                {date.locale('ru').format('MMMM YYYY')}
              </div>
              <Calendar
                fullscreen={false}
                value={date}
                headerRender={() => null}
                mode="month"
                onSelect={(value, info) => {
                  if (info.source !== 'customize')
                    changeQuery({ date: value.format('YYYY-MM-DD'), tab: 'day' })
                }}
                fullCellRender={value => (
                  <button
                    type="button"
                    aria-label={`${value.format('DD.MM.YYYY')} · поставщиков: ${
                      dayCounts[value.format('YYYY-MM-DD')] || 0
                    }`}
                    onClick={event => {
                      event.stopPropagation()
                      changeQuery({ date: value.format('YYYY-MM-DD'), tab: 'day' })
                    }}
                    className={`delivery-calendar__day${
                      value.format('YYYY-MM-DD') === selectedDate
                        ? ' delivery-calendar__day--selected'
                        : ''
                    }${
                      value.format('YYYY-MM') !== month ? ' delivery-calendar__day--outside' : ''
                    }`}
                  >
                    <span>{value.date()}</span>
                    {dayCounts[value.format('YYYY-MM-DD')] ? (
                      <small>{dayCounts[value.format('YYYY-MM-DD')]}</small>
                    ) : (
                      <small className="delivery-calendar__empty-count">·</small>
                    )}
                  </button>
                )}
              />
              <Typography.Paragraph type="secondary" className="delivery-calendar__legend">
                Число под датой — количество поставщиков. Несколько заказов одного поставщика считаются один раз.
              </Typography.Paragraph>
              <section className="delivery-calendar__summary" aria-label="Состояние поставок за день" aria-busy={loading}>
                <div className="delivery-calendar__summary-heading">
                  <strong>Поставки за день</strong>
                  <span>{date.locale('ru').format('D MMMM')}</span>
                </div>
                <dl className="delivery-calendar__summary-list">
                  <div className="delivery-calendar__summary-row delivery-calendar__summary-row--accepted">
                    <dt><CheckCircleOutlined /><Tooltip title="Заказы и заявки с подтверждённой приёмкой в ERP на выбранную фактическую дату. Для заказов также учитывается полное поступление в 1С. Один документ считается один раз."><span>Принято</span></Tooltip></dt>
                    <dd>{loading || error ? '—' : quantity(stats.accepted)}</dd>
                  </div>
                  <div className="delivery-calendar__summary-row delivery-calendar__summary-row--planned">
                    <dt><ClockCircleOutlined /><Tooltip title="Заказы и заявки на выбранную плановую дату, приёмка которых ещё не подтверждена в ERP."><span>Ожидается</span></Tooltip></dt>
                    <dd>{loading || error ? '—' : quantity(stats.planned)}</dd>
                  </div>
                  <div className="delivery-calendar__summary-row delivery-calendar__summary-row--moved">
                    <dt><SwapOutlined /><Tooltip title="Из ожидаемых: заказы и заявки, перенесённые в ERP на выбранную дату относительно даты 1С. Уже включены в число ожидаемых."><span>Перенесено</span></Tooltip></dt>
                    <dd>{loading || error ? '—' : quantity(stats.moved)}</dd>
                  </div>
                </dl>
                {!loading && !error && stats.late > 0 && (
                  <div className="delivery-calendar__summary-late"><ExclamationCircleOutlined />Опаздывает: {quantity(stats.late)}</div>
                )}
                {(supplierFilter || searchTerm) && <small className="delivery-calendar__summary-filter">С учётом выбранных фильтров</small>}
              </section>
              <div className="delivery-calendar__source">
                План и остатки из 1С
                {loadedAt && (
                  <>
                    <br />
                    Обновлено {dayjs(loadedAt).format('HH:mm')}
                    {refreshing && ' · обновляем…'}
                  </>
                )}
              </div>
            </aside>
            <section className="delivery-calendar__content">
              <div className="delivery-calendar__content-header">
                <div>
                  <Typography.Title level={4}>
                    {date.locale('ru').format('D MMMM, dddd')}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    Ожидаемые поставки и история поступлений из 1С. Нажмите на документ для просмотра.
                  </Typography.Text>
                </div>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Поставщик, заказ или заявка"
                  value={filter}
                  onChange={event => setFilter(event.target.value)}
                />
              </div>
              <div className="delivery-calendar__supplier-filter"><Select allowClear showSearch optionFilterProp="label" placeholder="Все поставщики" value={supplierFilter || undefined}
                options={suppliers.map(row => ({ value: row.key, label: `${row.supplier || 'Поставщик не указан'} · ${row.count}` }))}
                onChange={value => { setSupplierFilter(value || ''); setPage(1); setSelectedKeys([]) }} style={{ minWidth: 260 }} />
                {!!selectedKeys.length && <Button type="text" onClick={() => setSelectedKeys([])}>Снять выделение ({selectedKeys.length})</Button>}
              </div>
              {rows.length ? (
                <>
                  {groups.map(group => {
                    const data = rows.filter(row => (row as any).supplier_key === group.key)
                    return <section key={`${selectedDate}:${group.key}`} className="delivery-calendar__group" aria-label={group.supplier}>
                      <header className="delivery-calendar__group-header"><div><strong>{group.supplier || 'Поставщик не указан'}</strong></div>
                        <Button icon={<UploadOutlined />} onClick={() => openGroup(group)} disabled={opening || !data.some(canReceiveDelivery)}>Загрузить документы</Button></header>
                      <SupplierFiles analyses={analysis.analyses.filter(item => item.delivery_keys?.some(key => data.some(delivery => delivery.key === key)) || data.some(delivery => delivery.key === item.delivery_key))} refresh={analysis.refresh} />
                      <SupplierOrders count={group.count} selected={data.filter(row => selectedKeys.includes(row.key)).length}>
                      <Table<Delivery> size="small" rowKey="key" columns={displayColumns} dataSource={data} pagination={false} scroll={{ x: 950 }}
                        rowSelection={{ selectedRowKeys: selectedKeys, preserveSelectedRowKeys: true,
                          getCheckboxProps: row => ({ disabled: !canReceiveDelivery(row), title: isDeliveryAccepted(row) ? 'Поставка уже принята' : !row.source_active ? 'Поставка больше не ожидается' : undefined }),
                          onCell: () => ({ onClick: event => event.stopPropagation() }),
                          onChange: keys => { const next = [...new Set([...selectedKeys.filter(key => !data.some(row => row.key === key)), ...keys.map(String)])]; if (next.length > 100) { message.warning('Для общего итога можно выбрать до 100 документов. Уточните фильтр или снимите часть выделения.'); return }; setSelectedKeys(next) } }}
                        onRow={row => ({ onClick: () => void openIntake(row), className: 'delivery-calendar__order-row' })} />
                      </SupplierOrders>
                    </section>
                  })}
                  <Pagination current={currentPage} pageSize={pageSize} total={total} showSizeChanger pageSizeOptions={[10, 25, 50]}
                    showTotal={value => `Поставщиков: ${value}`} onChange={(next, size) => { setPage(size !== pageSize ? 1 : next); setPageSize(size) }} />
                </>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    loading
                      ? ' '
                      : filter
                      ? 'По вашему запросу поставки не найдены'
                      : 'На эту дату поставок нет'
                  }
                />
              )}
              <DeliveryTotals month={month} date={selectedDate} search={searchTerm} supplier={supplierFilter} selectedKeys={selectedKeys} revision={loadedAt} />
            </section>
          </div>
        </Spin>
        <Modal title="Выберите поставщика" open={chooseSupplier} onCancel={() => setChooseSupplier(false)} footer={null}>
          <p>Поставщики на {date.format('DD.MM.YYYY')}. Можно приложить один УПД на несколько заказов или заявок.</p>
          <div className="delivery-calendar__supplier-picker">{awaitingSuppliers.map(supplier => <Button key={supplier.key} block icon={<UploadOutlined />} loading={opening} onClick={() => openGroup(supplier)}>{supplier.supplier || 'Поставщик не указан'}</Button>)}</div>
        </Modal>
        {intake && (
          <DeliveryIntakeModal
            key={intake.key}
            delivery={intake}
            resume={analysis.states[intake.intakes[0]?.review_id]?.status === 'upload'}
            onStarted={row => {
              setIntake(null)
              setAnalysesOpen(true)
              analysis.track(row)
              void load(false, true)
              message.success('Разбор начался. Можно загружать документы следующей поставки.')
            }}
            onClose={() => {
              setIntake(null)
              void load()
            }}
          />
        )}
        <Modal
          title="Плановая дата поставки"
          open={!!plan}
          onCancel={() => !savingPlan && setPlan(null)}
          footer={
            <Space>
              <Button disabled={savingPlan} onClick={() => setPlan(null)}>
                Отмена
              </Button>
              {plan?.date_source === 'erp' && (
                <Button loading={savingPlan} onClick={() => savePlan(true)}>
                  Вернуть дату 1С
                </Button>
              )}
              <Button type="primary" loading={savingPlan} onClick={() => savePlan()}>
                Сохранить
              </Button>
            </Space>
          }
        >
          <Typography.Paragraph>
            {plan?.supplier || 'Поставщик не указан'} · № {plan?.document_number}
          </Typography.Paragraph>
          <DatePicker
            value={planDate ? dayjs(planDate) : null}
            format="DD.MM.YYYY"
            placeholder="Дата пока не назначена"
            onChange={value => setPlanDate(value ? value.format('YYYY-MM-DD') : null)}
            style={{ width: '100%' }}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
            Дата меняется только в ERP. Исходная дата 1С:{' '}
            {plan?.source_date ? dayjs(plan.source_date).format('DD.MM.YYYY') : 'не указана'}.
          </Typography.Paragraph>
        </Modal>
      </div>
    </B2BLayout>
  )
}
